import { zodResolver } from "@hookform/resolvers/zod"
import {
    getGtmIdFromCT,
    getPdp,
    gtmAddPdpProductToCart,
    gtmAddToWishlist,
    gtmAdpageAddToCart,
    gtmRemoveFromWishlist,
} from "@jog/shared"
import type { PdpProduct, SimpleWishlist } from "@jog/types"
import type { ImageField } from "@prismicio/types"
import {
    type Dispatch,
    type MouseEventHandler,
    type PropsWithChildren,
    type SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { useForm } from "react-hook-form"
import { type SubmitHandler, UseFormReturn } from "react-hook-form/dist/types"
import { z } from "zod"

import { usePending } from "../components"
import type { CartStore } from "../stores"
import { useAddedProduct } from "./useAddedProduct"
import { getPdpPrice } from "./useProductDetail"

const cartSchema = z.object({
    products: z.array(z.object({ sku: z.string(), id: z.string() })),
})
type CartSchema = z.infer<typeof cartSchema>
type ProductData = Record<
    string,
    { prismic: { cover: ImageField; name: string; id: string; path: string }; ct?: PdpProduct }
>
export interface CoordSetAndLookContext {
    setProductData: Dispatch<SetStateAction<ProductData>>
    productData: ProductData
    form: UseFormReturn<CartSchema>
    selectedProducts: CartSchema["products"]
    total: number
    onSubmit: SubmitHandler<CartSchema>
}

const CoordSetAndLookContext = createContext<CoordSetAndLookContext>({} as CoordSetAndLookContext)
export const CoordSetAndLookProvider = ({
    children,
    products,
    addProductsToCart,
}: PropsWithChildren<{
    products: { id: string; cover: ImageField; name: string }[]
    addProductsToCart: CartStore["addProductsToCart"]
}>) => {
    const [productData, setProductData] = useState<ProductData>(
        products.reduce((acc, cur) => ({ ...acc, [cur.id]: { prismic: cur } }), {})
    )

    const form = useForm<CartSchema>({
        resolver: zodResolver(cartSchema),
        defaultValues: { products: [] },
    })
    const selectedProducts = form.watch("products").filter((i) => i.sku && i.id)
    const total = useMemo(
        () =>
            selectedProducts.reduce((pre, cur) => {
                const product = productData?.[cur.id]?.ct
                if (!product) return pre
                return pre + getPdpPrice(product).currentPrice
            }, 0),
        [selectedProducts, productData]
    )
    const { setPending } = usePending()
    const { addedProduct } = useAddedProduct()

    const onSubmit = useCallback<SubmitHandler<CartSchema>>(
        async (values, event) => {
            event?.preventDefault()
            const skus = values.products.filter((i) => i.sku && i.id)
            if (skus.length === 0) {
                const $form = event?.target as HTMLFormElement | undefined
                if ($form) {
                    $form.scrollIntoView({ behavior: "smooth", block: "center" })
                }
                return
            }
            setPending(true)
            await addProductsToCart(skus.map((i) => ({ sku: i.sku, quantity: 1 }))).finally(() => setPending(false))
            addedProduct(skus.map((i) => i.sku))
            values.products.forEach((i) => {
                const product = productData?.[i.id]?.ct
                if (product) {
                    gtmAddPdpProductToCart(product, window.location.href)
                    gtmAdpageAddToCart(product, i.sku, getGtmIdFromCT(product.attributes), window.location.href)
                }
            })
        },
        [addProductsToCart, addedProduct, productData, setPending]
    )
    return (
        <CoordSetAndLookContext.Provider
            value={{ productData, form, setProductData, selectedProducts, total, onSubmit }}>
            {children}
        </CoordSetAndLookContext.Provider>
    )
}

export const useCoordSetAndLook = () => {
    return useContext(CoordSetAndLookContext)
}

export const useCoordSetAndLookProduct = ({ prismic, ct }: CoordSetAndLookContext["productData"][string]) => {
    const skus = useMemo(() => {
        switch (ct?.sizeGroup.type) {
            case "size":
                return ct?.sizeGroup.sizes.map((i) => ({
                    sku: i.sku,
                    size: i.size,
                    disabled: !i.isOnStock || i.availableQuantity === 0,
                }))
            case "waist_length":
                return ct?.sizeGroup.sizes.map((i) => ({
                    sku: i.sku,
                    size: `${i.waist}/${i.length}`,
                    disabled: !i.isOnStock || i.availableQuantity === 0,
                }))
            default:
                return []
        }
    }, [ct])
    const [pending, setPending] = useState(true)
    const { setProductData } = useCoordSetAndLook()
    useEffect(() => {
        getPdp(prismic.id)
            .then((ct) => setProductData((prev) => ({ ...prev, [prismic.id]: { prismic, ct } })))
            .catch(() => {})
            .finally(() => setPending(false))
    }, [prismic, setProductData])
    return { skus, pending }
}

export const useCoordSetAndLookWishlist = (
    {
        addWishlistItem,
        removeWishlistItem,
        simpleWishlist,
    }: {
        addWishlistItem: (productId: string) => Promise<void>
        removeWishlistItem: (lineItemId: string) => Promise<void>
        simpleWishlist: SimpleWishlist
    },
    { prismic, ct }: CoordSetAndLookContext["productData"][string]
) => {
    const lineItemId = useMemo(
        () => simpleWishlist?.lineItems?.find((lineItem) => lineItem.productId === ct?.id)?.id,
        [simpleWishlist, ct]
    )
    const onFavorite = useCallback<MouseEventHandler>(
        async (e) => {
            e.preventDefault()
            if (!ct) return
            const price = (ct.firstPrice || ct.price)?.centAmount || 0
            const gtmPrice = +(price / 100).toFixed(2)
            const gtmItem = {
                id: getGtmIdFromCT(ct?.attributes || []),
                name: prismic.name,
                price: gtmPrice,
            }
            if (!lineItemId) {
                gtmAddToWishlist([gtmItem])
                await addWishlistItem(ct.id)
            } else {
                gtmRemoveFromWishlist([gtmItem])
                await removeWishlistItem(lineItemId)
            }
        },
        [ct, prismic, lineItemId, addWishlistItem, removeWishlistItem]
    )
    return { onFavorite, lineItemId }
}
